import React, { forwardRef, useImperativeHandle, useState } from 'react';
import {
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  Space,
  Row,
  Col,
  Card
} from 'antd';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { ExtendedSearchOrdersParams } from '../types';
import { getStatusOptions } from '../utils/orderUtils';

const { RangePicker } = DatePicker;
const { Option } = Select;

export interface OrderSearchFormProps {
  /** 初始搜索参数 */
  initialParams?: Partial<ExtendedSearchOrdersParams>;
  /** 搜索提交回调 */
  onSearch?: (params: Partial<ExtendedSearchOrdersParams>) => void;
  /** 重置回调 */
  onReset?: () => void;
  /** 是否显示高级搜索 */
  showAdvanced?: boolean;
  /** 是否正在加载 */
  loading?: boolean;
  /** 是否禁用 */
  disabled?: boolean;
}

export interface OrderSearchFormRef {
  /** 重置表单 */
  reset: () => void;
  /** 提交表单 */
  submit: () => void;
  /** 获取表单值 */
  getValues: () => Partial<ExtendedSearchOrdersParams>;
  /** 设置表单值 */
  setValues: (values: Partial<ExtendedSearchOrdersParams>) => void;
}

const OrderSearchForm = forwardRef<OrderSearchFormRef, OrderSearchFormProps>(
  ({ initialParams, onSearch, onReset, showAdvanced = false, loading = false, disabled = false }, ref) => {
    const { t } = useTranslation(['modules', 'common']);
    const [form] = Form.useForm();

    // 初始化表单值
    React.useEffect(() => {
      if (initialParams) {
        form.setFieldsValue(initialParams);
      }
    }, [form, initialParams]);

    // 暴露ref方法
    useImperativeHandle(ref, () => ({
      reset: () => {
        form.resetFields();
        onReset?.();
      },
      submit: () => {
        form.submit();
      },
      getValues: () => {
        return form.getFieldsValue();
      },
      setValues: (values) => {
        form.setFieldsValue(values);
      }
    }));

    // 处理表单提交
    const handleSubmit = (values: any) => {
      const params: Partial<ExtendedSearchOrdersParams> = {};

      // 基础搜索条件 - 仅保留后端支持的字段
      if (values.customer_id) params.customer_id = values.customer_id;
      if (values.status) params.status = values.status;

      // 日期范围处理
      if (values.date_range && values.date_range.length === 2) {
        params.start_date = values.date_range[0].format('YYYY-MM-DD');
        params.end_date = values.date_range[1].format('YYYY-MM-DD');
      }

      onSearch?.(params);
    };

    // 处理重置
    const handleReset = () => {
      form.resetFields();
      onReset?.();
    };

    // 状态选项
    const statusOptions = getStatusOptions();

    return (
      <Card size="small" style={{ marginBottom: 8 }}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            ...initialParams
          }}
        >
          {/* 基础搜索条件 - 紧凑布局 */}
          <Row gutter={[16, 8]} align="middle">

            <Col xs={24} sm={12} md={6} lg={4}>
              <Form.Item
                name="customer_id"
                label={t('modules:order.query.search.customerId', '客户ID')}
                style={{ marginBottom: 0 }}
              >
                <Input
                  placeholder={t('modules:order.query.search.customerIdPlaceholder', '客户ID')}
                  allowClear
                  disabled={disabled}
                  size="small"
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={6} lg={4}>
              <Form.Item
                name="status"
                label={t('modules:order.query.search.status', '状态')}
                style={{ marginBottom: 0 }}
              >
                <Select
                  placeholder={t('modules:order.query.search.statusPlaceholder', '状态')}
                  allowClear
                  disabled={disabled}
                  size="small"
                >
                  {statusOptions.map(option => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>


            <Col xs={24} sm={12} md={6} lg={4}>
              <Form.Item
                name="date_range"
                label={t('modules:order.query.search.dateRange', '日期')}
                style={{ marginBottom: 0 }}
              >
                <RangePicker
                  style={{ width: '100%' }}
                  disabled={disabled}
                  format="YYYY-MM-DD"
                  size="small"
                />
              </Form.Item>
            </Col>



            {/* 操作按钮 */}
            <Col xs={24} sm={24} md={12} lg={8}>
              <Form.Item style={{ marginBottom: 0 }}>
                <Space>
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={<SearchOutlined />}
                    loading={loading}
                    disabled={disabled}
                    size="small"
                  >
                    {t('modules:order.query.search.button', '搜索')}
                  </Button>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={handleReset}
                    disabled={disabled}
                    size="small"
                  >
                    {t('modules:order.query.search.reset', '重置')}
                  </Button>
                </Space>
              </Form.Item>
            </Col>
          </Row>

        </Form>
      </Card>
    );
  }
);

OrderSearchForm.displayName = 'OrderSearchForm';

export default OrderSearchForm;